/**
 * 2.0 app pass, Part 4 (type that scales) — the root switch for `type_ramp_v1`.
 *
 * Mounted app-level beside <DocumentTitle /> (App.tsx), renders null. While the
 * flag reads ON it writes two attributes on <html>:
 *   · `data-type-ramp`  — the app-wide switch. tokens.css re-aims five tokens
 *     under it, global.css sets the root and body size under it, and
 *     BottomTab.module.css puts the tab label on the ramp under it.
 *   · `data-type-scale="ax"` — set if and only if the flag is ON and the
 *     computed root font size is at least 25.6px (1.6 x 16, "AX1 up" in Part 4
 *     ruling 4). tokens.css turns labels sentence case with no tracking under
 *     it, through `--label-case` and `--track-label` only (no JS contract).
 * Flag OFF (or the read not answered: `useServerFlag` fails closed) neither
 * attribute exists, so every selector above is unreachable and the app
 * renders exactly as it did before this file existed.
 *
 * The web setter is the stand-in for the native shell, which will write the
 * same attribute once the iOS text-size reading lands (device spike first).
 * Nothing here writes `--os-text-scale` and no plugin is involved.
 *
 * It re-reads the root size on mount, `resize` and `visibilitychange`, and
 * removes both attributes on unmount. The hook sits first: there is no early
 * return anywhere above it (React error #310).
 */
import { useEffect } from 'react';
import { useServerFlag } from '../../hooks/useServerFlag';

/** The attribute names this component writes. BottomTab.typeRamp.css.test.ts
 *  imports these and asserts the real CSS files select on exactly them. */
export const TYPE_RAMP_ATTR = 'data-type-ramp';
export const TYPE_SCALE_ATTR = 'data-type-scale';
/** The one value `data-type-scale` takes. */
export const TYPE_SCALE_AX = 'ax';
/** The computed root font size (px) at which labels leave uppercase:
 *  1.6 times the 16px default. */
export const AX_ROOT_MIN_PX = 25.6;

export function TypeRampRoot(): null {
  // The flag id is a literal on purpose: tests/config-flag-export-rake.test.js
  // derives the keys `/api/config` must export from literals at call sites.
  const on = useServerFlag('type_ramp_v1');

  useEffect(() => {
    const root = document.documentElement;
    if (!on) {
      root.removeAttribute(TYPE_RAMP_ATTR);
      root.removeAttribute(TYPE_SCALE_ATTR);
      return undefined;
    }

    root.setAttribute(TYPE_RAMP_ATTR, '');

    const syncScale = (): void => {
      const rootPx = parseFloat(window.getComputedStyle(root).fontSize);
      if (Number.isFinite(rootPx) && rootPx >= AX_ROOT_MIN_PX) {
        root.setAttribute(TYPE_SCALE_ATTR, TYPE_SCALE_AX);
      } else {
        root.removeAttribute(TYPE_SCALE_ATTR);
      }
    };
    syncScale();
    window.addEventListener('resize', syncScale);
    document.addEventListener('visibilitychange', syncScale);

    return () => {
      window.removeEventListener('resize', syncScale);
      document.removeEventListener('visibilitychange', syncScale);
      root.removeAttribute(TYPE_RAMP_ATTR);
      root.removeAttribute(TYPE_SCALE_ATTR);
    };
  }, [on]);

  return null;
}
